import type React from 'react';

import {
  Age0Icon,
  Age18Icon,
  Age45Icon,
  Age60Icon,
  AgeUnknownIcon,
} from '@vlabs/icons';
import type { WithDataTestid, WithClassNameProp } from '@vlabs/uikit/types';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import { AttributeCard } from './attribute-card/AttributeCard';

const ageGroups = [
  {
    from: 0,
    to: 17,
    get label() {
      return i18n.t('shared:attribute_cards.age.under_18');
    },
    icon: <Age0Icon />,
  },
  {
    from: 18,
    to: 44,
    get label() {
      return i18n.t('shared:attribute_cards.age.from_18_to_44');
    },
    icon: <Age18Icon />,
  },
  {
    from: 45,
    to: 60,
    get label() {
      return i18n.t('shared:attribute_cards.age.from_45_to_60');
    },
    icon: <Age45Icon />,
  },
  {
    from: 61,
    to: 100,
    get label() {
      return i18n.t('shared:attribute_cards.age.over_60');
    },
    icon: <Age60Icon />,
  },
];

interface AgeAttributeCardProps extends WithDataTestid, WithClassNameProp {
  age: number | null;
  size?: 'medium' | 'large';
}

export function AgeAttributeCard({
  age,
  className = undefined,
  'data-testid': dataTestId = 'AgeAttributeCard',
  ...props
}: AgeAttributeCardProps): React.JSX.Element {
  const { t } = useTranslation();

  const determinerAgeGroup = (value: number) => ageGroups.find(
    ({ from, to }) => from <= value && value <= to,
  );

  const ageGroup = age !== null ? determinerAgeGroup(age) : null;

  const description = age === null
    ? t('shared:attribute_cards.age.undefined')
    : ageGroup?.label;

  const icon = age === null
    ? <AgeUnknownIcon />
    : ageGroup?.icon;

  return (
    <AttributeCard
      className={className}
      data={age}
      data-testid={dataTestId}
      description={description}
      icon={icon}
      title={t('shared:attribute_cards.age.title')}
      {...props}
    >
      {age !== null && <span>{t('shared:attribute_cards.age.count', { count: age })}</span>}
    </AttributeCard>
  );
}
